import { ThumbsDown, ThumbsUp, X } from "lucide-react";
import { useEffect, useRef } from "react";

import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useIsStaff } from "@design-lab/shared/lib/config";
import { cn } from "@design-lab/shared/lib/tailwindcss";
import { Button, Modal, ModalBody, ModalHeader } from "@design-lab/shared/ui";
import { Textarea } from "@design-lab/shared/ui/textarea";
import { usePinningFeedback } from "./usePinningFeedback";

type FeedbackFormProps = {
  message: string;
  setMessage: (message: string) => void;
  isSending: boolean;
  positive: boolean | null;
  onCancel: () => void;
  onSubmit: () => void;
  autoFocus?: boolean;
};

function FeedbackForm({
  message,
  setMessage,
  isSending,
  positive,
  onCancel,
  onSubmit,
  autoFocus = false
}: FeedbackFormProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  return (
    <div className="space-y-2">
      <Textarea
        ref={textareaRef}
        value={message}
        onChange={event => setMessage(event.target.value)}
        placeholder="Optional details"
        rows={3}
        className="text-sm"
        disabled={isSending}
        data-testid="pinning-feedback-message"
      />
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" onPress={onCancel} isDisabled={isSending}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onPress={onSubmit}
          isDisabled={isSending || positive === null}
        >
          {isSending ? "Submitting..." : "Submit"}
        </Button>
      </div>
    </div>
  );
}

export function PinningFeedback() {
  const isStaff = useIsStaff();
  const { isDesktop } = useResponsive();
  const {
    positive,
    message,
    isOpen,
    status,
    handleSelectPositive,
    handleSelectNegative,
    handleCancel,
    handleSubmit,
    setMessage
  } = usePinningFeedback();
  const isSending = status === "sending";
  const isSuccess = status === "success";
  const shouldShowForm = isOpen && !isSuccess;
  const iconSizeClass = !isDesktop ? "h-4 w-4" : "h-5 w-5";

  const thumbBaseClass =
    "rounded-full border border-gray-200 text-gray-500 transition-colors hover:bg-gray-50";

  if (!isStaff) {
    return null;
  }

  return (
    <>
      <div className={cn("px-2 pb-2", isDesktop && "absolute bottom-14 right-4 z-20 p-0")}>
        <div
          className={cn(
            "rounded-lg border border-gray-200 bg-white p-3 shadow-sm",
            isDesktop ? "w-[280px]" : "w-full"
          )}
          data-testid="pinning-feedback"
        >
          {isSuccess ? (
            <div
              className={cn(
                "flex items-center text-xs text-green-700",
                isDesktop ? "min-h-9" : "min-h-7"
              )}
            >
              Thanks for the feedback.
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs font-medium text-gray-700">How is your session going?</div>
              <div className="flex items-center gap-1">
                <Button
                  variant="icon"
                  size={!isDesktop ? "icon_xs" : "icon"}
                  aria-pressed={positive === true}
                  className={cn(
                    thumbBaseClass,
                    positive === true &&
                      "border-green-200 bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-600"
                  )}
                  onPress={handleSelectPositive}
                  isDisabled={isSending}
                >
                  <ThumbsUp className={iconSizeClass} />
                </Button>
                <Button
                  variant="icon"
                  size={!isDesktop ? "icon_xs" : "icon"}
                  aria-pressed={positive === false}
                  className={cn(
                    thumbBaseClass,
                    positive === false &&
                      "border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-600"
                  )}
                  onPress={handleSelectNegative}
                  isDisabled={isSending}
                >
                  <ThumbsDown className={iconSizeClass} />
                </Button>
              </div>
            </div>
          )}

          {shouldShowForm && isDesktop && (
            <div className="mt-3">
              <FeedbackForm
                message={message}
                setMessage={setMessage}
                isSending={isSending}
                positive={positive}
                onCancel={handleCancel}
                onSubmit={handleSubmit}
                autoFocus
              />
            </div>
          )}
        </div>
      </div>

      {!isDesktop && (
        <Modal
          isOpen={shouldShowForm}
          onClose={handleCancel}
          className="absolute bottom-0 left-0 right-0 flex h-auto max-h-[90vh] w-full flex-col rounded-b-none rounded-t-2xl"
          backdropClassName="fixed z-50"
        >
          <ModalHeader className="justify-start border-0 p-4 pb-0 text-base font-medium text-gray-900">
            Share Your Feedback
            <button
              onClick={handleCancel}
              className="absolute right-4 cursor-pointer"
              data-testid="feedback-modal-close-button"
            >
              <X className="h-6 w-6" />
            </button>
          </ModalHeader>
          <ModalBody>
            <FeedbackForm
              message={message}
              setMessage={setMessage}
              isSending={isSending}
              positive={positive}
              onCancel={handleCancel}
              onSubmit={handleSubmit}
              autoFocus
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
}
